import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { BubbleChatIcon } from "@hugeicons-pro/core-stroke-rounded";
import { ApplicationProgress } from "api/applications";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { BadgeSize } from "shared/components/Badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { cx } from "shared/utils/classnames";
import { Application } from "types/application";
import {
  CLOSE_DELAY_MS,
  GRID_CLASS_NAME,
  MOTION_CSS,
  OPEN_DELAY_MS,
  STEP_STATE_ICON,
  STEP_STATE_LABEL,
  STEPS
} from "./constants";
import { ApplicantChatHandler, useApplicantProgressContext } from "./context";
import {
  buildPeople,
  hasAllProgress,
  householdProgress,
  lastActivityLabel,
  legendStates,
  PersonProgress
} from "./utils";

const chatTooltip = (person: PersonProgress) =>
  person.invited
    ? "No applicant to message yet"
    : `Message ${person.firstName || person.name}`;

const ChatButton = ({
  person,
  onChatClick
}: {
  person: PersonProgress;
  onChatClick: ApplicantChatHandler;
}) => (
  <IconButton
    size={IconButtonSize.XS}
    variant={IconButtonVariant.TEXT_BASE}
    HugeIcon={BubbleChatIcon}
    disabled={person.invited}
    tooltip={chatTooltip(person)}
    tooltipZIndex={12}
    onClick={() =>
      person.applicantId &&
      onChatClick({ applicantId: person.applicantId, name: person.name })
    }
  />
);

const StepCell = ({
  person,
  row,
  column,
  isUnavailable
}: {
  person: PersonProgress;
  row: number;
  column: number;
  isUnavailable?: boolean;
}) => {
  const step = STEPS[column];
  const state = person.steps?.[step.key];
  const style = {
    "--phc-cell-delay": `${140 + row * 50 + column * 45}ms`
  } as CSSProperties;

  return state ? (
    <span
      className={cx(
        "flex justify-center",
        state === "done" ? "phc-cell-done" : "phc-cell"
      )}
      style={style}
      title={`${step.label}: ${STEP_STATE_LABEL[state]}`}
      data-testid={`progress-step-${step.key}`}
      data-state={state}
    >
      <HugeiconsIcon
        icon={STEP_STATE_ICON[state].icon}
        size={16}
        className={cx(
          STEP_STATE_ICON[state].className,
          STEP_STATE_ICON[state].motion
        )}
      />
    </span>
  ) : (
    <span
      className="flex justify-center"
      title={`${step.label}: ${isUnavailable ? "Unavailable" : "Loading"}`}
    >
      <span
        className={cx(
          "h-4 w-4 rounded-full bg-alpha-100",
          !isUnavailable && "animate-pulse"
        )}
      />
    </span>
  );
};

const ApplicantProgressCard = ({
  application,
  progress,
  isProgressError,
  onChatClick
}: {
  application: Application;
  progress?: ApplicationProgress;
  isProgressError?: boolean;
  onChatClick?: ApplicantChatHandler;
}) => {
  const people = useMemo(
    () => buildPeople(application, progress),
    [application, progress]
  );
  const { done, total } = householdProgress(people);
  const now = Date.now();

  return (
    <div
      className="phc-card w-[760px] max-w-[calc(100vw-16px)] space-y-3 rounded-lg border border-alpha-100 bg-content-1000-inverted p-4 text-base text-content-1000 shadow-lg"
      data-testid="applicant-progress-card"
    >
      <style>{MOTION_CSS}</style>
      <div>
        <div
          className={cx(
            GRID_CLASS_NAME,
            "border-b border-alpha-40 pb-2 text-xxs font-medium text-content-400"
          )}
        >
          <span />
          {STEPS.map(({ key, label, short, Icon }) => (
            <span
              key={key}
              className="flex flex-col items-center gap-1 whitespace-nowrap"
              title={label}
            >
              <Icon className="h-4 w-4 stroke-content-300" />
              {short}
            </span>
          ))}
          <span className="self-end whitespace-nowrap text-center">Status</span>
          <span className="self-end whitespace-nowrap text-center">
            Last activity
          </span>
          <span />
        </div>
        {people.map((person, row) => (
          <div
            key={person.key}
            className={cx(
              GRID_CLASS_NAME,
              "phc-row border-b border-alpha-40 py-2 last:border-b-0"
            )}
            style={{ "--phc-row-delay": `${60 + row * 50}ms` } as CSSProperties}
            data-testid="applicant-progress-row"
          >
            <div className="min-w-0">
              <p
                className={cx(
                  "truncate text-xs font-bold",
                  person.invited ? "text-content-400" : "text-content-1000"
                )}
                title={person.name}
              >
                {person.name}
              </p>
              <p className="mt-0.5 truncate text-xxs text-content-400">
                {person.roleLabel}
              </p>
            </div>
            {STEPS.map((step, column) => (
              <StepCell
                key={step.key}
                person={person}
                row={row}
                column={column}
                isUnavailable={isProgressError}
              />
            ))}
            <span className="flex justify-center">
              <ApplicationStatusBadge
                status={person.status}
                type="applicant"
                size={BadgeSize.SM}
                className="!h-5 !px-1.5 !py-0 !text-xxs !leading-4"
              />
            </span>
            <span className="whitespace-nowrap text-center text-xxs text-content-500">
              {lastActivityLabel(person, now)}
            </span>
            <span className="flex justify-end">
              <Show if={onChatClick}>
                <ChatButton person={person} onChatClick={onChatClick!} />
              </Show>
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-start gap-4 text-xxs text-content-400">
        <div className="flex flex-1 flex-wrap gap-x-3 gap-y-1">
          {legendStates(people).map((state) => (
            <span key={state} className="inline-flex items-center gap-1">
              <HugeiconsIcon
                icon={STEP_STATE_ICON[state].icon}
                size={12}
                className={STEP_STATE_ICON[state].className}
              />
              {STEP_STATE_LABEL[state]}
            </span>
          ))}
        </div>
        <Show if={hasAllProgress(people)}>
          <p className="shrink-0 whitespace-nowrap" data-testid="steps-done">
            {done} of {total} steps done
          </p>
        </Show>
      </div>
    </div>
  );
};

export const ApplicantProgressHover = ({
  application,
  children
}: {
  application: Application;
  children: ReactNode;
}) => {
  const { progressByApplicationId, isProgressError, onChatClick } =
    useApplicantProgressContext();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const pointerOver = useRef({ trigger: false, card: false });

  const scheduleClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      if (pointerOver.current.trigger || pointerOver.current.card) return;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  }, []);
  const trackPointer = useCallback(
    (target: "trigger" | "card", isOver: boolean) => () => {
      pointerOver.current[target] = isOver;
      if (isOver) clearTimeout(closeTimer.current);
      else scheduleClose();
    },
    [scheduleClose]
  );
  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!next) return scheduleClose();
      clearTimeout(closeTimer.current);
      setOpen(true);
    },
    [scheduleClose]
  );

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <Tooltip
      placement="right-start"
      delay={{ open: OPEN_DELAY_MS }}
      open={open}
      onOpenChange={onOpenChange}
    >
      <TooltipTrigger
        className="flex h-full w-full min-w-0 items-center text-left"
        onMouseEnter={trackPointer("trigger", true)}
        onMouseLeave={trackPointer("trigger", false)}
        data-testid="applicant-progress-trigger"
      >
        <span className="block min-w-0 underline decoration-alpha-300 decoration-dotted underline-offset-4">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent
        chrome={false}
        className="z-[11]"
        onMouseEnter={trackPointer("card", true)}
        onMouseLeave={trackPointer("card", false)}
        onClick={(event) => event.stopPropagation()}
      >
        <ApplicantProgressCard
          application={application}
          progress={progressByApplicationId.get(application.id)}
          isProgressError={isProgressError}
          onChatClick={onChatClick?.(application.id)}
        />
      </TooltipContent>
    </Tooltip>
  );
};
